/**
 * Analytics Portal V1 — Named visitors (Phase 16, plan §8.5, §10.5, §11 endpoint 9).
 *
 * The highest-privacy surface in the portal, so this component is deliberately inert until asked:
 *   - It renders on the Profile, Business and Capital dashboards only (Summary, Events and
 *     Resources never mount it).
 *   - It is collapsed by default and issues **no** request until the viewer opens it, so identity
 *     data is never fetched speculatively.
 *   - Without the CAPITAL `named_visitors` capability it shows the upgrade state and makes no
 *     request at all — the server also enforces this, so the UI is not the gate.
 *   - Rows live in React Query's in-memory cache only: nothing is written to localStorage,
 *     sessionStorage, IndexedDB or a cookie, nothing is logged, and no visitor appears in a chart
 *     label, tooltip, page title or URL. Changing entity or range changes the query key, so stale
 *     rows are discarded rather than shown against the wrong period.
 */
import React, { useEffect, useState } from "react";
import { ChevronDown, ChevronUp, Loader2, Lock, Search, Users, X } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { fileUrl } from "@/lib/api";
import { useNamedVisitors } from "@/hooks/useAnalyticsData";
import { useReferences } from "@/hooks/useReferences";
import { AvailabilityNotice } from "./AvailabilityNotice";

const SORTS = [
  { value: "recent", label: "Most recent" },
  { value: "visits", label: "Most visits" },
];

const EMPTY_FILTERS = { q: "", industry: "", state: "", country: "" };
const ANY = "__any__";

/** One bounded reference-list filter. Values come from the shared reference bundle, never typed. */
const FilterSelect = ({ label, value, options, onChange, testId }) => (
  <Select value={value || ANY} onValueChange={(next) => onChange(next === ANY ? "" : next)}>
    <SelectTrigger className="h-9 w-full sm:w-40" aria-label={label} data-testid={testId}>
      <SelectValue placeholder={label} />
    </SelectTrigger>
    <SelectContent className="max-h-72">
      <SelectItem value={ANY}>{`Any ${label.toLowerCase()}`}</SelectItem>
      {(options || []).map((option) => (
        <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
      ))}
    </SelectContent>
  </Select>
);

const initials = (name) => String(name || "?").trim().slice(0, 1).toUpperCase();

const formatDay = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined,
      { day: "numeric", month: "short", year: "numeric" });
  } catch {
    return "—";
  }
};

export const NamedVisitorPanel = ({ entityType, entityId, capabilities, range }) => {
  const [open, setOpen] = useState(false);
  const [sort, setSort] = useState("recent");
  // Phase 19 filters. Draft state is what the viewer is typing; `filters` is what has been asked
  // for. Both are component state only — nothing is written to any browser storage.
  const [draft, setDraft] = useState(EMPTY_FILTERS);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [page, setPage] = useState(1);
  const allowed = Boolean(capabilities?.named_visitors);
  const { references } = useReferences();

  // A new entity or period must never show the previous one's people.
  useEffect(() => {
    setOpen(false);
    setDraft(EMPTY_FILTERS);
    setFilters(EMPTY_FILTERS);
    setPage(1);
  }, [entityId, entityType]);

  const applyFilters = (next) => {
    setDraft(next);
    setFilters(next);
    setPage(1);                       // any filter change resets pagination
  };

  const active = Object.values(filters).some((value) => value);

  const query = useNamedVisitors({
    entityType, entityId, range, sort, page, filters,
    enabled: open && allowed && Boolean(entityId),
  });
  const data = query.data || null;

  if (!entityId) return null;

  return (
    <section className="space-y-3" data-testid="analytics-named-visitors"
             aria-labelledby="analytics-named-visitors-heading">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 id="analytics-named-visitors-heading"
              className="flex items-center gap-2 text-base font-[620] text-foreground md:text-lg">
            <Users className="h-4 w-4 text-primary" aria-hidden="true" /> Named visitors
          </h2>
          <p className="mt-0.5 text-sm text-muted-foreground">
            Signed-in members who viewed this page. Anonymous and privately browsing visitors stay
            anonymous, and no browsing detail, message or search is ever shown.
          </p>
        </div>
        {allowed && (
          <Button variant="secondary" size="sm" onClick={() => setOpen((value) => !value)}
                  aria-expanded={open} aria-controls="analytics-named-visitors-body"
                  data-testid="analytics-named-visitors-toggle">
            {open ? <ChevronUp className="mr-1.5 h-4 w-4" aria-hidden="true" />
                  : <ChevronDown className="mr-1.5 h-4 w-4" aria-hidden="true" />}
            {open ? "Hide visitors" : "Show visitors"}
          </Button>
        )}
      </div>

      {!allowed ? (
        <Card className="flex flex-col gap-2 p-5" data-testid="analytics-named-visitors-upsell">
          <p className="flex items-center gap-2 text-sm font-[620] text-foreground">
            <Lock className="h-4 w-4 text-primary" aria-hidden="true" />
            Named visitors are included with CAPITAL
          </p>
          <p className="text-sm text-muted-foreground">
            Your plan includes every aggregate dashboard. Upgrade to CAPITAL to see which
            signed-in members viewed this page.
          </p>
        </Card>
      ) : open && (
        <div id="analytics-named-visitors-body" className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            {SORTS.map((option) => (
              <Button key={option.value} size="sm"
                      variant={sort === option.value ? "default" : "secondary"}
                      onClick={() => { setSort(option.value); setPage(1); }}
                      aria-pressed={sort === option.value}
                      data-testid={`analytics-named-visitors-sort-${option.value}`}>
                {option.label}
              </Button>
            ))}
          </div>

          <div className="flex flex-col gap-2 rounded-xl border border-border bg-card p-3 sm:flex-row sm:flex-wrap sm:items-center"
               data-testid="analytics-named-visitors-filters">
            <form className="relative w-full sm:w-56"
                  onSubmit={(submitEvent) => { submitEvent.preventDefault(); applyFilters(draft); }}>
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground"
                      aria-hidden="true" />
              <Input value={draft.q} className="h-9 pl-8"
                     placeholder="Search by name" aria-label="Search visitors by name"
                     onChange={(changeEvent) => setDraft({ ...draft, q: changeEvent.target.value })}
                     onBlur={() => applyFilters(draft)}
                     data-testid="analytics-named-visitors-search" />
            </form>
            <FilterSelect label="Industry" value={filters.industry}
                          options={references.industries}
                          onChange={(value) => applyFilters({ ...draft, industry: value })}
                          testId="analytics-named-visitors-industry" />
            <FilterSelect label="State" value={filters.state} options={references.us_states}
                          onChange={(value) => applyFilters({ ...draft, state: value })}
                          testId="analytics-named-visitors-state" />
            <FilterSelect label="Country" value={filters.country} options={references.countries}
                          onChange={(value) => applyFilters({ ...draft, country: value })}
                          testId="analytics-named-visitors-country" />
            {active && (
              <Button variant="ghost" size="sm" onClick={() => applyFilters(EMPTY_FILTERS)}
                      data-testid="analytics-named-visitors-clear-filters">
                <X className="mr-1 h-3.5 w-3.5" aria-hidden="true" /> Clear filters
              </Button>
            )}
          </div>

          {data?.window_notice && (
            <AvailabilityNotice notice={data.window_notice}
                                testId="analytics-named-visitors-window-notice" />
          )}
          {data?.notice && (
            <AvailabilityNotice notice={data.notice} reason={data.reason}
                                testId="analytics-named-visitors-notice" />
          )}

          {query.isLoading ? (
            <p className="flex items-center gap-2 text-sm text-muted-foreground" role="status"
               data-testid="analytics-named-visitors-loading">
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Loading visitors…
            </p>
          ) : query.error ? (
            <div className="rounded-xl border border-border bg-card p-4" role="alert"
                 data-testid="analytics-named-visitors-error">
              <p className="text-sm text-foreground">{query.error.message}</p>
              <Button variant="secondary" size="sm" className="mt-2"
                      onClick={() => query.refetch()}
                      data-testid="analytics-named-visitors-retry">Try again</Button>
            </div>
          ) : !data?.rows?.length ? (
            <p className="rounded-xl border border-border bg-card px-4 py-3 text-sm text-muted-foreground"
               role="note" data-testid="analytics-named-visitors-empty">
              {active
                ? "No identified visitors match these filters in the selected period."
                : "No signed-in members viewed this page in the selected period."}
            </p>
          ) : (
            <>
              <ul className="divide-y divide-border overflow-hidden rounded-xl border border-border bg-card"
                  data-testid="analytics-named-visitors-list">
                {data.rows.map((row) => (
                  <li key={row.user_id} className="flex items-center gap-3 px-4 py-3"
                      data-testid={`analytics-named-visitor-${row.user_id}`}>
                    <Avatar className="h-9 w-9 shrink-0">
                      {row.image_url && <AvatarImage src={fileUrl(row.image_url)} alt="" />}
                      <AvatarFallback>{initials(row.display_name)}</AvatarFallback>
                    </Avatar>
                    <div className="min-w-0 flex-1">
                      {row.slug ? (
                        <a href={`/p/${row.slug}`} className="truncate text-sm font-[620] text-foreground hover:text-primary hover:underline">
                          {row.display_name}
                        </a>
                      ) : (
                        <span className="truncate text-sm font-[620] text-foreground">
                          {row.display_name}
                        </span>
                      )}
                      <p className="text-xs text-muted-foreground">
                        Last visit {formatDay(row.last_visit_at)}
                      </p>
                    </div>
                    <span className="shrink-0 text-sm font-[620] text-foreground">
                      {row.visit_count.toLocaleString()}
                      <span className="ml-1 text-xs font-normal text-muted-foreground">
                        {row.visit_count === 1 ? "visit" : "visits"}
                      </span>
                    </span>
                  </li>
                ))}
              </ul>
              <p className="text-xs text-muted-foreground"
                 data-testid="analytics-named-visitors-count">
                Showing {data.rows.length} of {data.total.toLocaleString()}
                {data.capped ? "+" : ""} identified visitors
                {active ? " matching these filters" : ""} in the selected period.
              </p>
            </>
          )}
        </div>
      )}
    </section>
  );
};
